import {useAuth} from '@/contexts/AuthContext';

/**
 * Returns helper functions to check Spatie permissions + scope restrictions.
 *
 * Permission names follow the convention: gest_{resource}_{action}
 * e.g. 'gest_resource_read', 'gest_tag_delete', 'gest_incident_create'
 *
 * Access scope logic:
 *  - Total access   → permissionDepartments and permissionResources are both empty
 *  - By department  → permissionDepartments has rows
 *  - Individual     → permissionResources has rows
 */
export const usePermissions = () => {
    const {user} = useAuth();

    const permissions = user?.permissions ?? [];
    const departments = user?.permissionDepartments ?? [];
    const resources = user?.permissionResources ?? [];

    // ── Direct permission checks ─────────────────────────────────────────────

    /** True if the user has been granted this Spatie permission directly. */
    const can = (permission) => permissions.includes(permission);

    /** True if the user has ALL of the listed permissions. */
    const canAll = (...perms) => perms.every(can);

    /** True if the user has AT LEAST ONE of the listed permissions. */
    const canAny = (...perms) => perms.some(can);

    // ── Scope helpers ─────────────────────────────────────────────────────────

    /** True = no department or resource restriction (full data access). */
    const isTotalAccess = departments.length === 0 && resources.length === 0;

    /** True = user is restricted to specific departments. */
    const isByDepartment = departments.length > 0 && resources.length === 0;

    /** True = user is restricted to individual resources/tags. */
    const isByResource = resources.length > 0;

    /**
     * True if the user can see data belonging to departmentId.
     * Always true for total-access users.
     */
    const canAccessDepartment = (departmentId) =>
        isTotalAccess || departments.includes(departmentId);

    /**
     * True if the user can see this specific resource/tag.
     * Falls back to department check when not in individual mode.
     */
    const canAccessResource = (resourceId, departmentId) => {
        if (isTotalAccess) return true;
        if (isByResource) return resources.includes(resourceId);
        if (isByDepartment && departmentId !== undefined)
            return departments.includes(departmentId);
        return false;
    };

    return {
        permissions,
        departments,
        resources,
        can,
        canAll,
        canAny,
        isTotalAccess,
        isByDepartment,
        isByResource,
        canAccessDepartment,
        canAccessResource,
    };
};